import { Box, FormControl, FormErrorMessage, FormHelperText, FormLabel, type FormLabelProps } from "@chakra-ui/react";
import { type FC, type KeyboardEvent, forwardRef, useEffect } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

// The smart caret intercepts every Backspace/Delete and erases a single character, ignoring modifiers.
// Selecting the range a line (Cmd) or word (Option, or Ctrl on Windows) shortcut would remove makes it
// erase that range instead. A word is a run of digits plus the formatting between it and the caret.
const shortcutDeletionRange = (event: KeyboardEvent<HTMLInputElement>, value: string, caret: number) => {
  const isWord = event.altKey || event.ctrlKey;

  if (event.key === "Backspace" && event.metaKey) return [0, caret];
  if (event.key === "Backspace" && isWord) return [value.slice(0, caret).search(/\d*\D*$/), caret];
  if (event.key === "Delete" && event.metaKey) return [caret, value.length];
  if (event.key === "Delete" && isWord) return [caret, value.length - value.slice(caret).replace(/^\D*\d*/, "").length];
  return null;
};

const selectShortcutDeletion = (event: KeyboardEvent<HTMLInputElement>) => {
  const input = event.currentTarget;
  const { selectionStart, selectionEnd, value } = input;
  if (selectionStart == null || selectionStart !== selectionEnd) return;

  const range = shortcutDeletionRange(event, value, selectionStart);
  if (range) input.setSelectionRange(range[0], range[1]);
};

export interface PhoneFieldProps {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  helperText?: string;
  label: string;
  error?: string;
  labelProps?: FormLabelProps;
  isDisabled?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  placeholder?: string;
  defaultCountry?: string;
}

export const PhoneField: FC<PhoneFieldProps> = forwardRef(
  (
    {
      name,
      value,
      onChange,
      label,
      helperText,
      isDisabled = false,
      isRequired = false,
      isInvalid = false,
      error,
      labelProps = {},
      placeholder = "Enter phone number",
      defaultCountry = "US",
      ...rest
    },
    ref,
  ) => {
    useEffect(() => {
      const countrySelect = document.querySelector(".PhoneInputCountrySelect");
      if (countrySelect) {
        countrySelect.setAttribute("tabindex", "-1");
      }
    }, []);

    return (
      <FormControl isDisabled={isDisabled} isRequired={isRequired} isInvalid={isInvalid} mt="1">
        <FormLabel mt="0" p="0" fontSize="md" {...labelProps}>
          {label}
        </FormLabel>
        <Box
          className={`PhoneInput ${isDisabled ? "PhoneInput--disabled" : ""}`}
          h="40px"
          display="block"
          w="100%"
          sx={{
            ".PhoneInputInput": {
              h: "40px",
              w: "full",
              fontSize: "md",
              lineHeight: "1.5",
              bg: isDisabled ? "gray.100" : "white",
              border: "1px solid",
              borderColor: "inherit",
              borderRadius: "5px",
              p: "10px 12px",
              opacity: isDisabled ? 0.6 : 1,
              cursor: isDisabled ? "not-allowed" : "text",
              _focus: {
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                zIndex: 1,
              },
              _disabled: {
                bg: "gray.100",
                opacity: 0.6,
                cursor: "not-allowed",
              },
            },
            ".PhoneInputCountry": {
              position: "relative",
              display: "flex",
              alignItems: "center",
              marginRight: "8px",
              opacity: isDisabled ? 0.6 : 1,
              pointerEvents: isDisabled ? "none" : "auto",
              width: "auto",
              minWidth: "32px",
              cursor: isDisabled ? "default" : "pointer",
            },
            ".PhoneInputCountryIcon": {
              position: "relative",
              display: "block",
              width: "24px",
              height: "16px",
              marginRight: isDisabled ? "8px" : "0",
              opacity: isDisabled ? 0.6 : 1,
              "& img": {
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            },
            ".PhoneInputCountrySelect": {
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: "100%",
              zIndex: 1,
              border: "none",
              opacity: 0,
              cursor: isDisabled ? "default" : "pointer",
              display: isDisabled ? "none" : "block",
            },
            ".PhoneInputCountrySelectArrow": {
              display: isDisabled ? "none" : "block",
              opacity: 0.6,
              marginLeft: "4px",
            },
          }}>
          <PhoneInput
            ref={ref as any}
            name={name}
            value={value}
            disabled={isDisabled}
            onChange={onChange as any}
            onKeyDown={selectShortcutDeletion}
            defaultCountry={defaultCountry as any}
            placeholder={placeholder}
            countryCallingCodeEditable={false}
            countrySelectTabIndex={-1}
            {...rest}
          />
        </Box>
        <FormErrorMessage>{error}</FormErrorMessage>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    );
  },
);

// Add display name for React DevTools
PhoneField.displayName = "PhoneField";

// Export validation helper
export { isValidPhoneNumber };
